import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonFab,
  IonFabButton,
  IonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonList,
  IonItem,
  IonChip,
  IonAlert,
  IonSearchbar
} from '@ionic/react'
import { 
  addOutline,
  calendarOutline,
  analyticsOutline,
  arrowBackOutline,
  saveOutline,
  timeOutline,
  createOutline,
  trashOutline,
  videocamOutline,
  callOutline,
  locationOutline,
  searchOutline,
  closeOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playBackOutline,
  playForwardOutline,
  checkmarkCircleOutline,
  playOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import { CoachingSession } from '../../types'
import Avatar from '../../components/Avatar'

interface Coachee {
  id: string
  full_name: string
  email: string
  profile_picture_url?: string
  profile_picture_visibility?: string
}

const SessionsPage: React.FC = () => {
  const { user, isCoach } = useAuthStore()
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState<'sessions' | 'insights'>('sessions')
  const [sessions, setSessions] = useState<CoachingSession[]>([])
  const [coachees, setCoachees] = useState<Coachee[]>([])
  const [loading, setLoading] = useState(true)
  const [showSessionModal, setShowSessionModal] = useState(false)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null)
  const [editingSession, setEditingSession] = useState<CoachingSession | null>(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [sessionToUpdateStatus, setSessionToUpdateStatus] = useState<CoachingSession | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  // Form state
  const [formData, setFormData] = useState<{
    coachee_id: string
    title: string
    date: string
    time: string
    duration_minutes: number
    status: 'scheduled' | 'accepted' | 'in_progress' | 'completed' | 'cancelled'
    notes?: string
  }>({
    coachee_id: '',
    title: '',
    date: '',
    time: '',
    duration_minutes: 60,
    status: 'scheduled',
    notes: ''
  })

  // Helper functions
  const formatDateTime = (date: string, time: string) => {
    const sessionDate = new Date(`${date}T${time}`)
    return {
      date: sessionDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: sessionDate.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }
  }

  const getStatusColor = (status: CoachingSession['status']) => {
    switch (status) {
      case 'scheduled': return 'warning'
      case 'accepted': return 'success'
      case 'in_progress': return 'primary'
      case 'completed': return 'success'
      case 'cancelled': return 'danger'
      default: return 'primary'
    }
  }

  const getStatusText = (status: CoachingSession['status']) => {
    switch (status) {
      case 'scheduled': return t('sessions.statusScheduled', 'Scheduled')
      case 'accepted': return t('sessions.statusAccepted', 'Accepted')
      case 'in_progress': return t('sessions.statusInProgress', 'In Progress')
      case 'completed': return t('sessions.statusCompleted', 'Completed')
      case 'cancelled': return t('sessions.statusCancelled', 'Cancelled')
      default: return status
    }
  }

  // Fetch sessions
  const fetchSessions = async () => {
    if (!user) return
    
    // Don't fetch if we're currently deleting to prevent race conditions
    if (isDeleting) {
      console.log('Skipping fetch - deletion in progress')
      return
    }

    try {
      setLoading(true)
      console.log('Fetching sessions for user:', user.id, 'Role:', user.role)
      
      let query
      
      if (isCoach()) {
        // For coaches: get coachee information
        query = supabase
          .from('coaching_sessions')
          .select(`
            *,
            coachee:profiles!coaching_sessions_coachee_id_fkey (
              id,
              full_name,
              email,
              profile_picture_url,
              profile_picture_visibility
            )
          `)
          .eq('coach_id', user.id)
          .order('date', { ascending: false })
          .order('start_time', { ascending: false })
        console.log('Fetching as coach for coach_id:', user.id)
      } else {
        // For coachees: get coach information
        query = supabase
          .from('coaching_sessions')
          .select(`
            *,
            coach:profiles!coaching_sessions_coach_id_fkey (
              id,
              full_name,
              email,
              profile_picture_url,
              profile_picture_visibility
            )
          `)
          .eq('coachee_id', user.id)
          .order('date', { ascending: false })
          .order('start_time', { ascending: false })
        console.log('Fetching as coachee for coachee_id:', user.id)
      }

      const { data, error } = await query

      if (error) throw error

      console.log('Fetched sessions from server:', data?.length || 0)
      console.log('Session IDs and statuses:', data?.map(s => ({ id: s.id, status: s.status })) || [])

      setSessions(data || [])
      console.log('Sessions state updated with:', data?.length || 0, 'sessions')
    } catch (error) {
      console.error('Error fetching sessions:', error)
      setToastMessage('Failed to load sessions')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  // Fetch coachees (for coaches only)
  const fetchCoachees = async () => {
    if (!user || !isCoach()) return

    try {
      const { data, error } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          coachee:profiles!coach_coachee_relationships_coachee_id_fkey (
            id,
            full_name,
            email,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .eq('coach_id', user.id)
        .eq('status', 'active')

      if (error) throw error

      const coacheesList = data?.map((rel: any) => rel.coachee).filter(Boolean) || []
      setCoachees(coacheesList)
    } catch (error) {
      console.error('Error fetching coachees:', error)
    }
  }

  // Open session modal
  const openSessionModal = (session?: CoachingSession) => {
    if (session) {
      setEditingSession(session)
      setFormData({
        coachee_id: session.coachee_id,
        title: session.title,
        date: session.date,
        time: session.start_time,
        duration_minutes: session.duration_minutes,
        status: session.status,
        notes: session.notes || ''
      })
    } else {
      setEditingSession(null)
      setFormData({
        coachee_id: coachees.length > 0 ? coachees[0].id : '',
        title: '',
        date: new Date().toISOString().split('T')[0],
        time: '10:00',
        duration_minutes: 60,
        status: 'scheduled',
        notes: ''
      })
    }
    setShowSessionModal(true)
  }

  // Save session
  const saveSession = async () => {
    if (!user || !isCoach()) return

    try {
      setSubmitting(true)

      const sessionData = {
        coach_id: user.id,
        coachee_id: formData.coachee_id,
        title: formData.title,
        date: formData.date,
        start_time: formData.time,
        duration_minutes: formData.duration_minutes,
        status: formData.status,
        notes: formData.notes || null
      }

      if (editingSession) {
        const { error } = await supabase
          .from('coaching_sessions')
          .update(sessionData)
          .eq('id', editingSession.id)

        if (error) throw error
        setToastMessage('Session updated successfully')
      } else {
        const { error } = await supabase
          .from('coaching_sessions')
          .insert(sessionData)

        if (error) throw error
        setToastMessage('Session created successfully')
      }

      setShowToast(true)
      setShowSessionModal(false)
      fetchSessions()
    } catch (error) {
      console.error('Error saving session:', error)
      setToastMessage('Failed to save session')
      setShowToast(true)
    } finally {
      setSubmitting(false)
    }
  }

  // Delete session
  const deleteSession = async (sessionId: string) => {
    if (!user || !isCoach()) {
      console.log('Delete prevented: user not authenticated or not a coach')
      return
    }

    console.log('Attempting to delete session:', sessionId)
    console.log('Current user ID:', user.id)
    console.log('Current sessions before delete:', sessions.length)

    try {
      setIsDeleting(true)

      // First, let's verify the session exists and belongs to this coach
      const { data: sessionCheck, error: checkError } = await supabase
        .from('coaching_sessions')
        .select('id, coach_id, title')
        .eq('id', sessionId)
        .single()

      if (checkError) {
        console.error('Error checking session:', checkError)
        throw new Error('Session not found')
      }

      console.log('Session to delete:', sessionCheck)

      if (sessionCheck.coach_id !== user.id) {
        throw new Error('You can only delete your own sessions')
      }

      // Immediately remove from local state BEFORE database delete
      // This prevents any race conditions with refetching
      setSessions(prevSessions => {
        const updated = prevSessions.filter(session => session.id !== sessionId)
        console.log('Pre-emptively removed from local state, count:', updated.length)
        return updated
      })

      // Now perform the delete in database
      const { error, count } = await supabase
        .from('coaching_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('coach_id', user.id)

      if (error) {
        console.error('Supabase delete error:', error)
        // Restore the session if delete failed
        fetchSessions()
        throw error
      }

      console.log('Database delete successful, rows affected:', count)
      setToastMessage('Session deleted successfully')
      setShowToast(true)

    } catch (error) {
      console.error('Error deleting session:', error)
      setToastMessage(error instanceof Error ? error.message : 'Failed to delete session')
      setShowToast(true)
    } finally {
      setIsDeleting(false)
    }
  }

  // Update session status (for coachees)
  const updateSessionStatus = async (newStatus: CoachingSession['status']) => {
    if (!sessionToUpdateStatus || !user) return

    try {
      setSubmitting(true)

      console.log('Updating session status:', {
        sessionId: sessionToUpdateStatus.id,
        currentStatus: sessionToUpdateStatus.status,
        newStatus,
        userId: user.id
      })

      const { data, error } = await supabase
        .from('coaching_sessions')
        .update({ status: newStatus })
        .eq('id', sessionToUpdateStatus.id)
        .eq('coachee_id', user.id) // Ensure coachee can only update their own sessions
        .select()

      if (error) throw error

      console.log('Status update successful:', data)

      // Immediately update the local state for better UX
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionToUpdateStatus.id 
            ? { ...session, status: newStatus }
            : session
        )
      )

      setToastMessage(`Session ${getStatusText(newStatus)} successfully`)
      setShowToast(true)
      setShowStatusModal(false)
      setSessionToUpdateStatus(null)
      
      // Also refresh from database to ensure consistency
      console.log('Fetching updated sessions from database...')
      await fetchSessions()
      console.log('Sessions refetch completed')
    } catch (error) {
      console.error('Error updating session status:', error)
      setToastMessage('Failed to update session status')
      setShowToast(true)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle status chip click for coachees
  const handleStatusChipClick = (session: CoachingSession) => {
    if (!isCoach()) {
      setSessionToUpdateStatus(session)
      setShowStatusModal(true)
    }
  }

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  useEffect(() => {
    if (user) {
      fetchSessions()
      if (isCoach()) {
        fetchCoachees()
      }
    }
  }, [user, isCoach])

  // Filter and pagination logic
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = 
      session.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (isCoach() && session.coachee?.full_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (isCoach() && session.coachee?.email?.toLowerCase().includes(searchQuery.toLowerCase()))

    const matchesStatus = statusFilter === 'all' || session.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + itemsPerPage)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  return (
    <IonPage>
      <PageHeader title={isCoach() ? t('sessions.sessionManagement', 'Session Management') : t('sessions.mySessions', 'My Sessions')} />
      <IonContent>
        {/* Tab Switcher for Coaches */}
        {isCoach() && (
          <div className="px-4 pt-4">
            <IonSegment 
              value={activeTab} 
              onIonChange={(e) => setActiveTab(e.detail.value as any)}
            >
              <IonSegmentButton value="sessions">
                <IonIcon icon={calendarOutline} />
                <IonLabel>{t('sessions.title', 'Sessions')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="insights">
                <IonIcon icon={analyticsOutline} />
                <IonLabel>{t('sessions.insights', 'Insights')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <div className="px-4 py-4">
            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center justify-between text-base">
                    <div className="flex items-center">
                      <IonIcon icon={calendarOutline} className="mr-2" />
                      {isCoach() ? t('sessions.coachingSessions', 'Coaching Sessions') : t('sessions.mySessions', 'My Sessions')}
                    </div>
                    {isCoach() && (
                      <IonButton 
                        size="small" 
                        fill="clear"
                        onClick={() => openSessionModal()}
                      >
                        <IonIcon icon={addOutline} slot="icon-only" />
                      </IonButton>
                    )}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {/* Search and Filter Controls */}
                  <div className="mb-4 space-y-3">
                    <IonSearchbar
                      value={searchQuery}
                      onIonInput={(e: CustomEvent) => handleSearchChange(e.detail.value!)}
                      placeholder={t('sessions.search', 'Search sessions...')}
                      debounce={300}
                    />
                    
                    <div className="flex items-center space-x-2">
                      <IonSelect
                        value={statusFilter}
                        onIonChange={(e) => handleStatusFilterChange(e.detail.value)}
                        placeholder={t('sessions.filterByStatus', 'Filter by status')}
                        interface="popover"
                        className="flex-1"
                      >
                        <IonSelectOption value="all">{t('sessions.allStatuses', 'All Statuses')}</IonSelectOption>
                        <IonSelectOption value="scheduled">{t('sessions.statusScheduled', 'Scheduled')}</IonSelectOption>
                        <IonSelectOption value="accepted">{t('sessions.statusAccepted', 'Accepted')}</IonSelectOption>
                        <IonSelectOption value="in_progress">{t('sessions.statusInProgress', 'In Progress')}</IonSelectOption>
                        <IonSelectOption value="completed">{t('sessions.statusCompleted', 'Completed')}</IonSelectOption>
                        <IonSelectOption value="cancelled">{t('sessions.statusCancelled', 'Cancelled')}</IonSelectOption>
                      </IonSelect>
                      
                      {(searchQuery || statusFilter !== 'all') && (
                        <IonButton 
                          fill="clear" 
                          size="small"
                          onClick={() => {
                            setSearchQuery('')
                            setStatusFilter('all')
                            setCurrentPage(1)
                          }}
                        >
                          <IonIcon icon={closeOutline} />
                        </IonButton>
                      )}
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-8">
                      <IonSpinner />
                    </div>
                  ) : filteredSessions.length > 0 ? (
                    <>
                      <IonList>
                      {paginatedSessions.map((session) => {
                        const dateTime = formatDateTime(session.date, session.start_time)
                        return (
                          <IonItem key={`${session.id}-${session.status}`} className="mb-3">
                            <div className="w-full py-2">
                              {/* Row 1: Avatar + Name */}
                              <div className="flex items-center mb-3">
                                {isCoach() && session.coachee && (
                                  <>
                                    <Avatar
                                      user={{
                                        id: session.coachee.id,
                                        full_name: session.coachee.full_name,
                                        email: session.coachee.email,
                                        profile_picture_url: session.coachee.profile_picture_url,
                                        profile_picture_visibility: session.coachee.profile_picture_visibility as any
                                      }}
                                      currentUserId={user?.id}
                                      relationship="coach"
                                      size="medium"
                                      className="w-10 h-10 mr-3"
                                    />
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-900">
                                        {session.coachee.full_name || session.coachee.email}
                                      </span>
                                      <IonChip 
                                        color={getStatusColor(session.status)}
                                        className="text-xs"
                                      >
                                        {getStatusText(session.status)}
                                      </IonChip>
                                    </div>
                                  </>
                                )}
                                {!isCoach() && session.coach && (
                                  <>
                                    <Avatar
                                      user={{
                                        id: session.coach.id,
                                        full_name: session.coach.full_name,
                                        email: session.coach.email,
                                        profile_picture_url: session.coach.profile_picture_url,
                                        profile_picture_visibility: session.coach.profile_picture_visibility as any
                                      }}
                                      currentUserId={user?.id}
                                      relationship="coachee"
                                      size="medium"
                                      className="w-10 h-10 mr-3"
                                    />
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-900">
                                        {session.coach.full_name || session.coach.email}
                                      </span>
                                      <IonChip 
                                        color={getStatusColor(session.status)}
                                        className={`text-xs ${!isCoach() ? 'cursor-pointer hover:opacity-75' : ''}`}
                                        onClick={() => handleStatusChipClick(session)}
                                      >
                                        {getStatusText(session.status)}
                                      </IonChip>
                                    </div>
                                  </>
                                )}
                                {!isCoach() && !session.coach && (
                                                                      <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-900">{t('sessions.sessionWithCoach', 'Session with Coach')}</span>
                                      <IonChip 
                                        color={getStatusColor(session.status)}
                                        className={`text-xs ${!isCoach() ? 'cursor-pointer hover:opacity-75' : ''}`}
                                        onClick={() => handleStatusChipClick(session)}
                                      >
                                        {getStatusText(session.status)}
                                      </IonChip>
                                    </div>
                                )}
                              </div>

                              {/* Row 2: Subject */}
                              <div className="mb-3">
                                <h3 className="text-lg font-bold text-gray-900">{session.title}</h3>
                              </div>

                              {/* Row 3: Date, time, duration */}
                              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                                <span>{dateTime.date}</span>
                                <span>{dateTime.time}</span>
                                <span>{session.duration_minutes} min</span>
                              </div>

                              {/* Row 4: Notes */}
                              {session.notes && (
                                <div className="mb-3">
                                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                                    {session.notes}
                                  </p>
                                </div>
                              )}

                              {/* Row 5: Action buttons (right aligned) */}
                              {isCoach() && (
                                <div className="flex justify-end gap-2">
                                  <IonButton 
                                    size="small" 
                                    fill="clear" 
                                    onClick={() => openSessionModal(session)}
                                  >
                                    <IonIcon icon={createOutline} slot="icon-only" />
                                  </IonButton>
                                  <IonButton 
                                    size="small" 
                                    fill="clear" 
                                    color="danger"
                                    onClick={() => {
                                      setSessionToDelete(session.id)
                                      setShowDeleteAlert(true)
                                    }}
                                  >
                                    <IonIcon icon={trashOutline} slot="icon-only" />
                                  </IonButton>
                                </div>
                              )}
                            </div>
                          </IonItem>
                        )
                      })}
                    </IonList>

                    {/* Pagination Controls */}
                    <div className="flex flex-col space-y-4 mt-4">
                      {/* Items per page selector and results info */}
                      <div className="flex flex-nowrap items-center justify-between text-sm">
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-600">{t('sessions.pagination.show', 'Show')}:</span>
                          <IonSelect
                            value={itemsPerPage}
                            onIonChange={(e) => handlePageSizeChange(e.detail.value)}
                            interface="popover"
                            className="w-16"
                          >
                            <IonSelectOption value={3}>3</IonSelectOption>
                            <IonSelectOption value={5}>5</IonSelectOption>
                            <IonSelectOption value={10}>10</IonSelectOption>
                            <IonSelectOption value={25}>25</IonSelectOption>
                            <IonSelectOption value={50}>50</IonSelectOption>
                          </IonSelect>
                        </div>
                        <div className="text-gray-600">
                          {t('sessions.pagination.showing', 'Showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredSessions.length)} {t('sessions.pagination.of', 'of')} {filteredSessions.length}
                        </div>
                      </div>

                      {/* Navigation controls */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between">
                          {/* Desktop navigation - full text */}
                          <div className="hidden sm:flex items-center space-x-2">
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(1)}
                              className="min-w-[60px]"
                            >
                              <IonIcon icon={playBackOutline} slot="start" />
                              {t('sessions.pagination.first', 'First')}
                            </IonButton>
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                              className="min-w-[80px]"
                            >
                              <IonIcon icon={chevronBackOutline} slot="start" />
                              {t('sessions.pagination.previous', 'Previous')}
                            </IonButton>
                          </div>

                          {/* Mobile navigation - icons only */}
                          <div className="flex sm:hidden items-center space-x-1">
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(1)}
                            >
                              <IonIcon icon={playBackOutline} />
                            </IonButton>
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                            >
                              <IonIcon icon={chevronBackOutline} />
                            </IonButton>
                          </div>

                          {/* Page indicator */}
                          <div className="flex items-center">
                            <span className="text-sm text-gray-600 hidden sm:block">
                              {t('sessions.pagination.page', 'Page')} {currentPage} {t('sessions.pagination.of', 'of')} {totalPages}
                            </span>
                            <span className="text-sm text-gray-600 sm:hidden">
                              {currentPage}/{totalPages}
                            </span>
                          </div>

                          {/* Desktop navigation - full text */}
                          <div className="hidden sm:flex items-center space-x-2">
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                              className="min-w-[60px]"
                            >
                              {t('sessions.pagination.next', 'Next')}
                              <IonIcon icon={chevronForwardOutline} slot="end" />
                            </IonButton>
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                              className="min-w-[60px]"
                            >
                              {t('sessions.pagination.last', 'Last')}
                              <IonIcon icon={playForwardOutline} slot="end" />
                            </IonButton>
                          </div>

                          {/* Mobile navigation - icons only */}
                          <div className="flex sm:hidden items-center space-x-1">
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                            >
                              <IonIcon icon={chevronForwardOutline} />
                            </IonButton>
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                            >
                              <IonIcon icon={playForwardOutline} />
                            </IonButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : sessions.length > 0 ? (
                  <div className="text-center py-8">
                    <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">{t('sessions.noResults', 'No sessions match your search criteria')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      onClick={() => {
                        setSearchQuery('')
                        setStatusFilter('all')
                        setCurrentPage(1)
                      }}
                    >
                      {t('sessions.clearFilters', 'Clear Filters')}
                    </IonButton>
                  </div>
                ) : (
                    <div className="text-center py-8">
                      <IonIcon icon={calendarOutline} className="text-6xl text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {isCoach() ? t('sessions.noSessions', 'No sessions scheduled') : t('sessions.noSessionsYet', 'No sessions yet')}
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {isCoach() 
                          ? t('sessions.noSessionsDescription', 'Create your first coaching session to get started')
                          : t('sessions.noSessionsCoacheeDescription', 'Your coach will schedule sessions with you')
                        }
                      </p>
                      {isCoach() && (
                        <IonButton onClick={() => openSessionModal()}>
                          <IonIcon icon={addOutline} slot="start" />
                          {t('sessions.scheduleSession', 'Schedule Session')}
                        </IonButton>
                      )}
                    </div>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            {/* Insights Tab (Coaches Only) */}
            {activeTab === 'insights' && isCoach() && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center text-base">
                    <IonIcon icon={analyticsOutline} className="mr-2" />
                    {t('sessions.coacheeInsights', 'Coachee Insights')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="text-center py-8">
                    <IonIcon icon={analyticsOutline} className="text-6xl text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sessions.coacheeInsights', 'Coachee Insights')}</h3>
                    <p className="text-gray-500 mb-6">
                      {t('sessions.insightsDescription', 'View your coachees\' reflection entries, goals, and progress insights. The detailed insights feature is available in the dedicated Insights tab.')}
                    </p>
                    <IonButton 
                      expand="block" 
                      fill="solid" 
                      routerLink="/app/insights"
                      className="mb-4"
                    >
                      <IonIcon icon={analyticsOutline} slot="start" />
                      {t('sessions.goToInsights', 'Go to Insights')}
                    </IonButton>
                    <p className="text-sm text-gray-400">
                      {t('sessions.insightsNote', 'Or use the Insights tab in the bottom navigation.')}
                    </p>
                  </div>
                </IonCardContent>
              </IonCard>
            )}
          </div>
        )}

        {/* FAB for creating sessions (coaches only, sessions tab) */}
        {isCoach() && activeTab === 'sessions' && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => openSessionModal()}>
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </IonFab>
        )}

        {/* Session Modal */}
        <IonModal isOpen={showSessionModal} onDidDismiss={() => setShowSessionModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{editingSession ? t('sessions.editSession', 'Edit Session') : t('sessions.newSession', 'New Session')}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowSessionModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton 
                  onClick={saveSession}
                  disabled={!formData.title || !formData.coachee_id || submitting}
                >
                  <IonIcon icon={saveOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="space-y-4">
              {/* Coachee Selection */}
              <div>
                <IonLabel position="stacked">{t('sessions.coachee', 'Coachee')} *</IonLabel>
                <IonSelect
                  value={formData.coachee_id}
                  placeholder={t('sessions.selectCoachee', 'Select a coachee')}
                  onIonChange={(e) => handleInputChange('coachee_id', e.detail.value)}
                >
                  {coachees.map((coachee) => (
                    <IonSelectOption key={coachee.id} value={coachee.id}>
                      {coachee.full_name || coachee.email}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </div>

              {/* Title */}
              <div>
                <IonLabel position="stacked">{t('sessions.sessionTitle', 'Session Title')} *</IonLabel>
                <IonInput
                  value={formData.title}
                  placeholder={t('sessions.sessionTitlePlaceholder', 'e.g., Weekly Check-in')}
                  onIonInput={(e) => handleInputChange('title', e.detail.value!)}
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <IonLabel position="stacked">{t('sessions.date', 'Date')} *</IonLabel>
                  <IonInput
                    type="date"
                    value={formData.date}
                    onIonInput={(e) => handleInputChange('date', e.detail.value!)}
                  />
                </div>
                <div>
                  <IonLabel position="stacked">{t('sessions.time', 'Time')} *</IonLabel>
                  <IonInput
                    type="time"
                    value={formData.time}
                    onIonInput={(e) => handleInputChange('time', e.detail.value!)}
                  />
                </div>
              </div>

              {/* Duration */}
              <div>
                <IonLabel position="stacked">{t('sessions.durationMinutes', 'Duration (minutes)')}</IonLabel>
                <IonSelect
                  value={formData.duration_minutes}
                  onIonChange={(e) => handleInputChange('duration_minutes', e.detail.value)}
                >
                  <IonSelectOption value={30}>{t('sessions.durations.30min', '30 minutes')}</IonSelectOption>
                  <IonSelectOption value={45}>{t('sessions.durations.45min', '45 minutes')}</IonSelectOption>
                  <IonSelectOption value={60}>{t('sessions.durations.60min', '60 minutes')}</IonSelectOption>
                  <IonSelectOption value={90}>{t('sessions.durations.90min', '90 minutes')}</IonSelectOption>
                  <IonSelectOption value={120}>{t('sessions.durations.120min', '120 minutes')}</IonSelectOption>
                </IonSelect>
              </div>

              {/* Status */}
              <div>
                <IonLabel position="stacked">{t('sessions.status', 'Status')}</IonLabel>
                <IonSelect
                  value={formData.status}
                  onIonChange={(e) => handleInputChange('status', e.detail.value)}
                >
                  <IonSelectOption value="scheduled">{t('sessions.statusScheduled', 'Scheduled')}</IonSelectOption>
                  <IonSelectOption value="accepted">{t('sessions.statusAccepted', 'Accepted')}</IonSelectOption>
                  <IonSelectOption value="in_progress">{t('sessions.statusInProgress', 'In Progress')}</IonSelectOption>
                  <IonSelectOption value="completed">{t('sessions.statusCompleted', 'Completed')}</IonSelectOption>
                  <IonSelectOption value="cancelled">{t('sessions.statusCancelled', 'Cancelled')}</IonSelectOption>
                </IonSelect>
              </div>

              {/* Notes */}
              <div>
                <IonLabel position="stacked">{t('sessions.notes', 'Notes')}</IonLabel>
                <IonTextarea
                  value={formData.notes}
                  placeholder={t('sessions.notesPlaceholder', 'Session notes, prep materials, etc...')}
                  rows={4}
                  onIonInput={(e) => handleInputChange('notes', e.detail.value!)}
                />
              </div>
            </div>
          </IonContent>
        </IonModal>

        {/* Status Update Modal (for coachees) */}
        <IonModal isOpen={showStatusModal} onDidDismiss={() => setShowStatusModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('sessions.updateStatus', 'Update Session Status')}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowStatusModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {sessionToUpdateStatus && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">{sessionToUpdateStatus.title}</h3>
                  <p className="text-gray-600">
                    {new Date(sessionToUpdateStatus.date).toLocaleDateString()} at {sessionToUpdateStatus.start_time}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('sessions.currentStatus', 'Current status')}: {getStatusText(sessionToUpdateStatus.status)}
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {t('sessions.selectNewStatus', 'Select new status')}:
                  </h4>
                  
                  {sessionToUpdateStatus.status === 'scheduled' && (
                    <>
                      <IonButton
                        expand="block"
                        fill="solid"
                        color="success"
                        onClick={() => updateSessionStatus('accepted')}
                        disabled={submitting}
                      >
                        <IonIcon icon={checkmarkCircleOutline} slot="start" />
                        {t('sessions.acceptSession', 'Accept Session')}
                      </IonButton>
                      
                      <IonButton
                        expand="block"
                        fill="outline"
                        color="danger"
                        onClick={() => updateSessionStatus('cancelled')}
                        disabled={submitting}
                      >
                        <IonIcon icon={closeOutline} slot="start" />
                        {t('sessions.declineSession', 'Decline Session')}
                      </IonButton>
                    </>
                  )}

                  {sessionToUpdateStatus.status === 'accepted' && (
                    <>
                      <IonButton
                        expand="block"
                        fill="solid"
                        color="primary"
                        onClick={() => updateSessionStatus('in_progress')}
                        disabled={submitting}
                      >
                        <IonIcon icon={playOutline} slot="start" />
                        {t('sessions.startSession', 'Start Session')}
                      </IonButton>
                      
                      <IonButton
                        expand="block"
                        fill="outline"
                        color="danger"
                        onClick={() => updateSessionStatus('cancelled')}
                        disabled={submitting}
                      >
                        <IonIcon icon={closeOutline} slot="start" />
                        {t('sessions.cancelSession', 'Cancel Session')}
                      </IonButton>
                    </>
                  )}

                  {sessionToUpdateStatus.status === 'in_progress' && (
                    <>
                      <IonButton
                        expand="block"
                        fill="solid"
                        color="success"
                        onClick={() => updateSessionStatus('completed')}
                        disabled={submitting}
                      >
                        <IonIcon icon={checkmarkCircleOutline} slot="start" />
                        {t('sessions.markCompleted', 'Mark as Completed')}
                      </IonButton>
                      
                      <IonButton
                        expand="block"
                        fill="outline"
                        color="danger"
                        onClick={() => updateSessionStatus('cancelled')}
                        disabled={submitting}
                      >
                        <IonIcon icon={closeOutline} slot="start" />
                        {t('sessions.cancelSession', 'Cancel Session')}
                      </IonButton>
                    </>
                  )}

                  {(sessionToUpdateStatus.status === 'completed' || sessionToUpdateStatus.status === 'cancelled') && (
                    <div className="text-center py-4">
                      <p className="text-gray-500">
                        {t('sessions.statusFinal', 'This session status is final and cannot be changed.')}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </IonContent>
        </IonModal>

        {/* Delete Confirmation Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => {
            setShowDeleteAlert(false)
            setSessionToDelete(null)
          }}
          header={t('sessions.deleteSession', 'Delete Session')}
          message={t('sessions.deleteConfirm', 'Are you sure you want to delete this session? This action cannot be undone.')}
          buttons={[
            {
              text: t('common.cancel', 'Cancel'),
              role: 'cancel',
              handler: () => {
                setShowDeleteAlert(false)
                setSessionToDelete(null)
              }
            },
            {
              text: t('common.delete', 'Delete'),
              role: 'destructive',
              handler: () => {
                if (sessionToDelete) {
                  deleteSession(sessionToDelete)
                  setSessionToDelete(null)
                  setShowDeleteAlert(false)
                }
                return false // Prevent automatic dismissal
              }
            }
          ]}
        />

        {/* Toast for notifications */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  )
}

export default SessionsPage
